<template>
    <div>
        <!-- delete -->
        <delete-dialog
            ref="deleteDialog"
            @on-delete="getDoubleVisits()"
            :success-message="$t('message.data-is-deleted')"
        >
            {{ $t('are-you-sure-you-want-to') }} <span class="red--text">{{ $t('delete') }}</span> 
            {{$t('DoubleVisitsReports.this-double-visit-report') + $t('?')}}
        </delete-dialog>

        <v-btn 
            v-if="isSuperAdmin || permissionsUser.WeeklyReports === roleName.action"
            small
            class="mx-2 mb-2"
            color="light-blue white--text"
            :to="{
                name: 'double-visit-report'
            }"
        >
            <v-icon class="me-2"> mdi-plus-circle-outline </v-icon>
            {{ $t('DoubleVisitsReports.add-new-report') }}
        </v-btn>
        <!-- filter -->
        <v-form @submit.prevent="isFiltered = true; submit()">
            <v-row class="mb-1">
                <v-col md="2" sm="3" cols="6" class="pe-1 pb-1">
                    <v-autocomplete
                        v-model="employeeId"
                        :items="medicalRepsBrief.filter(c => c.employeeType  !== userType.medicalRep)"
                        item-value="id"
                        item-text="name"
                        :loading="submitLoading"
                        :disabled="loading"
                        :label="$t('inputs.employee')"
                        outlined
                        dense
                        hide-details
                    />
                </v-col>

                <!-- medicalRepId -->
                <v-col md="2" sm="3" cols="6" class="px-1 pb-1">
                    <v-autocomplete
                        v-model="medicalRepId"
                        :items="medicalRepsBrief.filter(c => c.employeeType  === userType.medicalRep)"
                        item-value="id"
                        item-text="name"
                        :loading="submitLoading"
                        :disabled="loading"
                        :label="$t('inputs.medical-rep')"
                        outlined
                        dense
                        hide-details
                    />
                </v-col>

                <!-- start date -->
                <v-col md="2" sm="3" cols="6" class="px-1 pb-1">
                    <bee-date-picker
                        v-model="startDate"
                        :input-label="$t('inputs.start-date')"
                        elevation="2"
                        menu-picker
                        dense
                        hide-details
                        :input-props="{
                            outlined: true,
                            dense: true,
                            clearable: true,
                        }"
                        :loading="loading"
                        :disabled="loading"
                        :allowed-dates="disableDates"
                    ></bee-date-picker>
                </v-col>

                <!-- end date -->
                <v-col md="2" sm="3" cols="6" class=" ps-1 pb-1">
                    <bee-date-picker
                        v-model="endDate"
                        :input-label="$t('inputs.end-date')"
                        elevation="2"
                        menu-picker
                        dense
                        hide-details
                        :input-props="{
                            outlined: true,
                            dense: true,
                            clearable: true,
                        }"
                        :allowed-dates="disableDates"
                    ></bee-date-picker>
                </v-col>

                <v-spacer />

                <v-col cols="auto" align-self="center" class="d-flex justify-end px-1 pb-1">
                    <v-btn
                        color="success white--text"
                        small
                        :disabled="loading || isAllFieldsEmpty"
                        class="px-sm-4 mx-2"
                        @click="getDoubleVisits()"
                    >                        
                        <v-icon> mdi-magnify </v-icon>
                        {{$t('search')}}
                    </v-btn>

                    <v-btn
                        color="alert-color white--text"
                        small
                        :loading="loading"
                        :disabled="!isFiltered"
                        class="px-sm-8"
                        @click="reset(); getDoubleVisits()"
                    >                        
                        {{$t('reset')}}
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
        
        <!-- table -->
        <bee-handy-table
            :headers="headers"
            :items="doubleVisits"
            :loading="loading"
            dense
            fixed-header
            :height="$vuetify.breakpoint.height - 200"
            pagination-on-scroll
            zebra
        >
             <!-- actions -->
            <template v-slot:item.actions="{ item }">
                <tooltip :text="$t('DoubleVisitsReports.view-report')" top>
                    <!-- v-if="permissionsUser.WeeklyReports !== roleName.action" -->
                    <v-btn
                        icon
                        small
                        :color="isSuperAdmin || permissionsUser.WeeklyReports !== roleName.action  ? 'success' : 'edit-color'"
                        :to="{
                            name: 'double-visit-report',
                            params: {
                                id: item.id
                            },
                            query: {
                                from: 'all',
                                preview: true
                            }
                        }"
                    >
                        <v-icon>
                           mdi-eye-outline
                        </v-icon>
                    </v-btn>
                </tooltip>
                    
                <tooltip
                    v-if="isSuperAdmin || permissionsUser.WeeklyReports === roleName.action"
                    :text="$t('DoubleVisitsReports.delete-report')"
                    top
                >
                    <v-btn
                        icon
                        small
                        @click="$refs.deleteDialog.activeDeleteDialog(item.id, 'doubleVisit')"
                        color="alert-color"
                    >
                        <v-icon>mdi-delete</v-icon>
                    </v-btn>
                </tooltip>
            </template>
        </bee-handy-table>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { roleName, userType } from '@/helpers/enums'
import Tooltip from '@/components/Tooltip.vue'
import DeleteDialog from '@/components/DeleteDialog'
import moment from 'moment'
import { isDisabledFriday } from '@/configs/global.config'
import { disableDates } from '@/helpers/functions'
export default {
    components: {
        Tooltip,
        DeleteDialog
    },

    data() {
        return {
            startDate: null,
            endDate: null,
            roleName,
            loading: false,
            submitLoading: false,
            medicalRepId: null,
            employeeId: this.$route.query.employeeId ? this.$route.query.employeeId : null,
            userType,
            isFiltered: false,
            isDisabledFriday,
            disableDates
        }
    },

    computed: {
        headers() {
            const headers = [
                {
                    text: this.$t('headers.date'),
                    name: 'date',
                    value: 'date',
                    setValueExpr: val => this.moment(val).format('YYYY-MM-DD')
                },
                // for role more than team lader
                {
                    text: this.$t('headers.employee-name'),
                    name: 'SupervisorName',
                    value: 'employeeId',
                    setValueExpr: val => this.getMedicalRepById(val)?.name
                },
                {
                    text: this.$t('headers.medical-rep'),
                    name: 'medicalRep',
                    value: 'medicalRepId',
                    setValueExpr: val => this.getMedicalRepById(val)?.name
                },
                {
                    text: '',
                    name: 'actions',
                    value: 'actions'
                }
            ]
            return headers
        },

        isAllFieldsEmpty() {
            return !this.startDate && !this.endDate && !this.employeeId && !this.medicalRepId;
        },

        ...mapState({
            permissionsUser: state => state.auth.youPermissions,
            doubleVisits: state => state.doubleVisit.doubleVisits,
            medicalRepsBrief: state => state.medicalreps.medicalRepsBrief,
            isSuperAdmin: state => state.auth.isSuperAdmin,
        }),

        ...mapGetters({
            getMedicalRepById: 'medicalreps/getMedicalRepById'
        })
    },

    methods: {
        moment,
        getDoubleVisits() {
            this.loading = true
            this.$store.dispatch('doubleVisit/fetchAll', { 
                medicalRepId: this.medicalRepId, 
                employeeId: this.employeeId,
                fromDate: this.startDate, 
                toDate: this.endDate 
            }).finally(() => {
                this.loading = false
            })
        },
        reset() {
            this.medicalRepId = null
            this.employeeId = null
            this.startDate = null
            this.endDate = null
            this.isFiltered = false;
        }
    },

    created() {
        Promise.all([
            !this.medicalRepsBrief.length ? this.$store.dispatch('medicalreps/fetchAllBrief', true) : null,

            this.getDoubleVisits()
        ])
    }
}
</script>
